import { z } from 'zod';
import { CircuitStepDaytime } from './CircuitStepDaytime';
import {
  TarificationPeriodSchema,
  TarificationSchema,
} from '../benefits/tarification/TarificationFormData';
import { PricingPartitionSchema } from '../benefits/hotel/HotelRoomPricingFormData';
import {
  HotelPensionPricingSchema,
  HotelPensionTypeEnum,
} from '@/types/benefits/hotel/HotelPensionPricingSchema';
import { HotelSupplementBaseSchema } from '@/types/benefits/hotel/HotelSupplementSchema';
import { AssociatedBenefitFrequencySchema } from '@/types/benefits/associated-benefit-frequency';
import { LOCALES } from '@/i18n';

const multilingualFieldSchema = <T extends z.ZodType>(type: T) =>
  z.record(z.enum(LOCALES), type.optional());

export const BenefitTranslationSchema = z
  .object({
    name: z.string().optional(),
    activity: z
      .object({
        shortDescription: z.string().optional(),
        longDescription: z.string().optional(),
      })
      .optional(),
    flight: z
      .object({
        shortDescription: z.string().optional(),
        longDescription: z.string().optional(),
      })
      .optional(),
    VehicleWithChauffeur: z
      .object({
        shortDescription: z.string().optional(),
        longDescription: z.string().optional(),
      })
      .optional(),
    hotel: z
      .object({
        shortDescriptionBody: z.string().optional(),
        longDescriptionBody: z.string().optional(),
        HotelRoomCategory: z
          .array(
            z
              .object({
                name: z.string().optional(),
                shortDescription: z.string().optional(),
                longDescription: z.string().optional(),
              })
              .optional(),
          )
          .optional(),
      })
      .optional(),
    carRental: z
      .object({
        shortDescription: z.string().optional(),
        longDescription: z.string().optional(),
        insurances: z
          .array(
            z.object({
              name: z.string().optional(),
              shortDescription: z.string().optional(),
              longDescription: z.string().optional(),
            }),
          )
          .optional(),
        taxes: z.array(z.object({ name: z.string().optional() })).optional(),
        options: z
          .array(
            z.object({
              name: z.string().optional(),
              description: z.string().optional(),
            }),
          )
          .optional(),
        oneWayFees: z
          .array(
            z.object({
              name: z.string().optional(),
              description: z.string().optional(),
            }),
          )
          .optional(),
        carCategories: z
          .array(
            z
              .object({
                category: z.string().optional(),
                model: z.string().optional(),
                description: z.string().optional(),
                included: z.string().optional(),
                notIncluded: z.string().optional(),
                insurances: z
                  .array(
                    z.object({
                      name: z.string().optional(),
                      shortDescription: z.string().optional(),
                      longDescription: z.string().optional(),
                    }),
                  )
                  .optional(),
                taxes: z
                  .array(z.object({ name: z.string().optional() }))
                  .optional(),
                options: z
                  .array(
                    z.object({
                      name: z.string().optional(),
                      description: z.string().optional(),
                    }),
                  )
                  .optional(),
                oneWayFees: z
                  .array(
                    z.object({
                      name: z.string().optional(),
                      description: z.string().optional(),
                    }),
                  )
                  .optional(),
              })
              .optional(),
          )
          .optional(),
      })
      .optional(),
  })
  .optional();

const CarRentalInsuranceSchema = z.object({
  id: z.string(),
  name: z.union([z.null(), z.string()]).optional(),
  shortDescription: z.union([z.null(), z.string()]).optional(),
  longDescription: z.union([z.null(), z.string()]).optional(),
  amount: z.union([z.null(), z.coerce.number()]).optional(),
  billingType: z.union([z.null(), z.enum(['perDay', 'perRental'])]).optional(),
});

const CarRentalOptionSchema = z.object({
  id: z.string(),
  name: z.union([z.null(), z.string()]).optional(),
  description: z.union([z.null(), z.string()]).optional(),
  amount: z.union([z.null(), z.coerce.number()]).optional(),
  billingType: z.union([z.null(), z.enum(['perDay', 'perRental'])]).optional(),
});

const CarRentalTaxSchema = z.object({
  id: z.string(),
  name: z.union([z.null(), z.string()]).optional(),
  amount: z.union([z.null(), z.coerce.number()]).optional(),
  billingType: z.union([z.null(), z.enum(['perDay', 'perRental'])]).optional(),
  required: z.union([z.null(), z.boolean()]).optional(),
});

const CarRentalOneWayFeeSchema = z.object({
  id: z.string(),
  name: z.union([z.null(), z.string()]).optional(),
  departureCity: z.union([z.null(), z.string()]).optional(),
  arrivalCity: z.union([z.null(), z.string()]).optional(),
  description: z.union([z.null(), z.string()]).optional(),
  amount: z.union([z.null(), z.coerce.number()]).optional(),
});

const BenefitDataSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  currency: z.string(),
  marginRate: z.coerce.number().optional(),
  supplier: z.object({
    marginRate: z.coerce.number().optional(),
  }),
  Pricing: z.array(TarificationSchema),
  activity: z
    .object({
      activityType: z.string().optional(),
      pricingType: z.enum(['byBenefit', 'byPerson']).optional(),
      longDescription: z.union([z.null(), z.string()]).optional(),
      shortDescription: z.union([z.null(), z.string()]).optional(),
      images: z.union([z.null(), z.array(z.string())]).optional(),
      specialTarif: z.boolean().optional(),
      childAgeFrom: z.union([z.null(), z.number()]).optional(),
      childAgeTo: z.union([z.null(), z.number()]).optional(),
    })
    .optional(),
  hotel: z
    .object({
      category: z.union([z.null(), z.string()]).optional(),
      mainImage: z.union([z.null(), z.string()]).optional(),
      images: z.union([z.null(), z.array(z.string())]).optional(),
      pricingType: z.enum(['byPerson', 'byRoom']).optional(),
      reducedPriceExists: z.boolean().optional(),
      isReduction: z.boolean().optional(),
      acceptsChildPricing: z.union([z.null(), z.boolean()]).optional(),
      childAgeFrom: z.union([z.null(), z.number()]).optional(),
      childAgeTo: z.union([z.null(), z.number()]).optional(),
      secondChildAgeFrom: z.union([z.null(), z.number()]).optional(),
      secondChildAgeTo: z.union([z.null(), z.number()]).optional(),
      shortDescriptionBody: z.union([z.null(), z.string()]).optional(),
      longDescriptionBody: z.union([z.null(), z.string()]).optional(),
      pricingPensionType: z.union([z.null(), z.string()]).optional(),
      shouldFillOtherPensionTypes: z.union([z.null(), z.boolean()]).optional(),
      areOtherPensionPricesCommon: z.union([z.null(), z.boolean()]).optional(),
      commonPensionPricings: z
        .array(HotelPensionPricingSchema)
        .optional()
        .default([]),
      shouldFillHotelSupplements: z.union([z.null(), z.boolean()]).optional(),
      shouldAssociateBenefits: z
        .union([z.null(), z.boolean(), z.enum(['true', 'false'])])
        .optional(),
      hotelSupplements: z
        .array(HotelSupplementBaseSchema)
        .optional()
        .default([]),
      associatedBenefits: z
        .array(
          z.object({
            id: z.string().optional(),
            benefitId: z.string(),
            frequency: AssociatedBenefitFrequencySchema,
            benefit: z
              .object({
                id: z.string(),
                name: z.string().optional(),
                code: z.union([z.number(), z.null()]).optional(),
                type: z.string(),
                country: z.string().nullable().optional(),
                supplier: z
                  .object({
                    id: z.string(),
                    name: z.string().nullable().optional(),
                    marginRate: z.number().optional(),
                  })
                  .optional(),
                Pricing: z.array(TarificationSchema).optional(),
                activity: z
                  .object({
                    activityType: z.string().optional(),
                    pricingType: z.enum(['byBenefit', 'byPerson']).optional(),
                    childAgeFrom: z.number().nullable().optional(),
                    childAgeTo: z.number().nullable().optional(),
                  })
                  .optional(),
              })
              .optional(),
          }),
        )
        .optional()
        .default([]),
      HotelRoomCategory: z.array(
        z.object({
          id: z.union([z.null(), z.string()]).optional(),
          name: z.union([z.null(), z.string()]).optional(),
          mainImage: z.union([z.null(), z.string()]).optional(),
          images: z.union([z.null(), z.array(z.string())]).optional(),
          shortDescription: z.union([z.null(), z.string()]).optional(),
          longDescription: z.union([z.null(), z.string()]).optional(),
          categoryPensionPricings: z
            .array(HotelPensionPricingSchema)
            .optional()
            .default([]),
          roomTypes: z.array(
            z.enum(['individual', 'double', 'triple', 'quadruple']),
          ),
          individualRoomOptions: z.array(z.number()),
          doubleRoomOptions: z.array(z.number()),
          tripleRoomOptions: z.array(z.number()),
          quadrupleRoomOptions: z.array(z.number()),
          pricings: z.array(
            z.object({
              periods: z.array(TarificationPeriodSchema),
              individualRoomRepartitions: z.array(PricingPartitionSchema),
              doubleRoomRepartitions: z.array(PricingPartitionSchema),
              tripleRoomRepartitions: z.array(PricingPartitionSchema),
              quadrupleRoomRepartitions: z.array(PricingPartitionSchema),
            }),
          ),
        }),
      ),
    })
    .optional(),
  carRental: z
    .object({
      displayGlobalForm: z.union([z.null(), z.boolean()]).optional(),
      shouldLimitInsurance: z.union([z.null(), z.boolean()]).optional(),
      insurances: z.array(CarRentalInsuranceSchema).optional(),
      options: z.array(CarRentalOptionSchema).optional(),
      taxes: z.array(CarRentalTaxSchema).optional(),
      oneWayFees: z.array(CarRentalOneWayFeeSchema).optional(),
      carCategories: z.array(
        z.object({
          id: z.union([z.null(), z.string()]).optional(),
          images: z.union([z.null(), z.array(z.string())]).optional(),
          pricings: z.array(TarificationSchema),
          maxCapacity: z.coerce.number().optional(),
          category: z.union([z.null(), z.string()]).optional(),
          model: z.union([z.null(), z.string()]).optional(),
          description: z.union([z.null(), z.string()]).optional(),
          additionalCost: z.union([z.null(), z.coerce.number()]).optional(),
          gearBoxType: z
            .union([z.null(), z.enum(['manual', 'automatic'])])
            .optional(),
          depositAmount: z.union([z.null(), z.coerce.number()]).optional(),
          maxNbLuggage: z.union([z.null(), z.coerce.number()]).optional(),
          minimumAge: z.union([z.null(), z.coerce.number()]).optional(),
          standardDeductableAmount: z
            .union([z.null(), z.coerce.number()])
            .optional(),
          airConditioning: z.union([z.null(), z.boolean()]).optional(),
          includedMileage: z.union([z.null(), z.coerce.number()]).optional(),
          isIncludedMileageUnlimited: z
            .union([z.null(), z.boolean()])
            .optional(),
          included: z.union([z.null(), z.string()]).optional(),
          notIncluded: z.union([z.null(), z.string()]).optional(),
          shouldLimitInsurance: z.union([z.null(), z.boolean()]).optional(),
          displayCategoryOptions: z.union([z.null(), z.boolean()]).optional(),
          insurances: z.array(CarRentalInsuranceSchema).optional(),
          options: z.array(CarRentalOptionSchema).optional(),
          taxes: z.array(CarRentalTaxSchema).optional(),
          oneWayFees: z.array(CarRentalOneWayFeeSchema).optional(),
        }),
      ),
    })
    .optional(),
  VehicleWithChauffeur: z
    .object({
      maxCapacity: z.coerce.number().optional(),
      shortDescription: z.union([z.null(), z.string()]).optional(),
      longDescription: z.union([z.null(), z.string()]).optional(),
      model: z.union([z.null(), z.string()]).optional(),
      images: z.union([z.null(), z.array(z.string())]).optional(),
    })
    .optional(),
  flight: z
    .union([
      z.null(),
      z.object({
        shortDescription: z.union([z.null(), z.string()]).optional(),
        longDescription: z.union([z.null(), z.string()]).optional(),
        specialTarif: z.boolean().optional(),
        childAgeFrom: z.union([z.null(), z.number()]).optional(),
        childAgeTo: z.union([z.null(), z.number()]).optional(),
      }),
    ])
    .optional(),
  city: z.string().optional(),
  indexFirstDay: z.union([z.number(), z.null()]).optional(),
  translations: multilingualFieldSchema(BenefitTranslationSchema).optional(),
});

export type BenefitFormData = z.infer<typeof BenefitDataSchema>;

const CircuitDayStepDisplayType = z.array(
  z.enum(['name', 'shortDescription', 'longDescription']),
);

const DayPeriodType = z
  .union([
    z.object({
      label: z.string(),
      value: z.nativeEnum(CircuitStepDaytime),
    }),
    z.null(),
  ])
  .optional();

const CircuitDayStepElementSchema = z.object({
  id: z.string(),
  standardBenefit: BenefitDataSchema.optional(),
  optionalBenefits: z.array(BenefitDataSchema),
  standardBenefitPensionType: HotelPensionTypeEnum.optional(),
  optionalBenefitsPensionTypes: z
    .record(z.string(), HotelPensionTypeEnum)
    .optional()
    .default({}),
  standardDisplays: CircuitDayStepDisplayType,
  optionalDisplays: CircuitDayStepDisplayType,
  time: DayPeriodType,
  nbDays: z.coerce.number().optional(),
  indexFirstDay: z.union([z.number(), z.null()]).optional(),
});

const CircuitDayStepSchema = z.object({
  transport: CircuitDayStepElementSchema,
  activities: CircuitDayStepElementSchema,
  meals: CircuitDayStepElementSchema,
  accommodations: CircuitDayStepElementSchema,
});

export const CircuitDaySchema = z.object({
  stepTitle: z.string().optional(),
  images: z.array(z.union([z.instanceof(Blob), z.string()])),
  description: z.string().optional(),
  steps: z.array(CircuitDayStepSchema),
});

export type CircuitDayFormData = z.infer<typeof CircuitDaySchema>;

export const ExtendedCircuitDaysSchema = z.object({
  circuitDays: z.array(CircuitDaySchema).min(1),
});

export type ExtendedCircuitDaysFormData = z.infer<
  typeof ExtendedCircuitDaysSchema
>;
